<template>
  <v-dialog v-model="isDialogOpen" max-width="850">
    <v-card class="pa-4">
      <v-card-title
      class="common-font-size title-position" id="tmc-blue">ReNewGov
      </v-card-title>
      <v-card-text>
        <v-row class="d-flex justify-space-around">
          <v-col cols="5" class="elevation-1 pa-4">
            <p class="text-center text-subtitle-1 header-class">Payment Details</p>
            <template v-if="forte2PaymentType === 'CREDIT'">
            <div class="input-field">
              <label for="forte2-card-number">Card Number</label>
              <input
                type="text"
                id="forte2-card-number"
                name="forte2-card-number"
                inputmode="numeric"
                v-model="cardNumber"
                placeholder="4111 1111 1111 1111"
              >
            </div>
            <div class="d-flex">
              <div class="input-field mr-2" style="flex: 1;">
                <label for="forte2-exp-month">Expiration (MM)</label>
                <input
                  type="text"
                  id="forte2-exp-month"
                  name="forte2-exp-month"
                  inputmode="numeric"
                  maxlength="2"
                  v-model="expireMonth"
                  placeholder="MM"
                >
              </div>
              <div class="input-field" style="flex: 1;">
                <label for="forte2-exp-year">Expiration (YY)</label>
                <input
                  type="text"
                  id="forte2-exp-year"
                  name="forte2-exp-year"
                  inputmode="numeric"
                  maxlength="4"
                  v-model="expireYear"
                  placeholder="YY"
                >
              </div>
            </div>
            <div class="d-flex">
              <div class="input-field mr-2" style="flex: 1;">
                <label for="forte2-cvv">CVV</label>
                <input
                  type="text"
                  id="forte2-cvv"
                  name="forte2-cvv"
                  inputmode="numeric"
                  maxlength="4"
                  v-model="cvv"
                  placeholder="123"
                >
              </div>
              <div class="input-field" style="flex: 1;">
                <label for="forte2-name">Name</label>
                <input
                  type="text"
                  id="forte2-name"
                  name="forte2-name"
                  v-model="nameOnCard"
                  placeholder="Name on card"
                >
              </div>
            </div>
            </template>
            <template v-if="forte2PaymentType === 'ECHECK'">
              <div class="input-field">
                <label for="forte2-routing-number">Routing Number</label>
                <input
                  type="text"
                  id="forte2-routing-number"
                  name="forte2-routing-number"
                  inputmode="numeric"
                  maxlength="9"
                  autocomplete="off"
                  :value="maskedEcheckValue('routingNumber')"
                  placeholder="123456789"
                  :disabled="echeckInputsLocked"
                  @input="onMaskedEcheckInput('routingNumber', $event)"
                  @focus="focusedEcheckField = 'routingNumber'"
                  @blur="focusedEcheckField = ''"
                  :class="{'invalid-input': routingNumberError}"
                >
              </div>
              <div v-if="routingNumberError" class="error-text">
                Routing number must be 9 digits.
              </div>
              <div class="input-field">
                <label for="forte2-account-number">Account Number</label>
                <input
                  type="text"
                  id="forte2-account-number"
                  name="forte2-account-number"
                  inputmode="numeric"
                  autocomplete="off"
                  :value="maskedEcheckValue('accountNumber')"
                  placeholder="Account number"
                  :disabled="echeckInputsLocked"
                  @input="onMaskedEcheckInput('accountNumber', $event)"
                  @focus="focusedEcheckField = 'accountNumber'"
                  @blur="focusedEcheckField = ''"
                >
              </div>
              <div class="input-field">
                <label for="forte2-confirm-account-number">Confirm Account Number</label>
                <input
                  type="text"
                  id="forte2-confirm-account-number"
                  name="forte2-confirm-account-number"
                  inputmode="numeric"
                  autocomplete="off"
                  :value="maskedEcheckValue('confirmAccountNumber')"
                  placeholder="Re-enter account number"
                  :disabled="echeckInputsLocked"
                  @input="onMaskedEcheckInput('confirmAccountNumber', $event)"
                  @focus="focusedEcheckField = 'confirmAccountNumber'"
                  @blur="focusedEcheckField = ''"
                  :class="{'invalid-input': accountMismatchError}"
                >
              </div>
              <div v-if="accountMismatchError" class="error-text">
                Account numbers do not match.
              </div>
              <div class="d-flex">
                <div class="input-field mr-2" style="flex: 1;">
                  <label for="forte2-account-type">Account Type</label>
                  <select
                    id="forte2-account-type"
                    name="forte2-account-type"
                    v-model="accountType"
                    :disabled="echeckInputsLocked"
                    @change="onEcheckFieldInput"
                  >
                    <option value="">Select</option>
                    <option value="c">Checking</option>
                    <option value="s">Savings</option>
                  </select>
                </div>
                <div class="input-field" style="flex: 1;">
                  <label for="forte2-name-on-account">Name on Account</label>
                  <input
                    type="text"
                    id="forte2-name-on-account"
                    name="forte2-name-on-account"
                    v-model="nameOnAccount"
                    placeholder="Name on account"
                    :disabled="echeckInputsLocked"
                    @input="onEcheckFieldInput"
                  >
                </div>
              </div>
            </template>
            <div class="input-field">
              <label for="forte2-phone-number">Phone Number (optional)</label>
              <input
                type="tel"
                id="forte2-phone-number"
                name="forte2-phone-number"
                v-model="phoneNumber"
                placeholder="(555) 123-4567"
                @input="validatePhoneNumber"
                :class="{'invalid-input': phoneNumberError}"
              >
            </div>
            <div v-if="phoneNumberError" class="error-text">
              Please enter a valid phone number (numbers, parentheses, hyphens only).
            </div>
            <div class="input-field">
              <label for="forte2-email">Email
                <span v-if="emailReceipt" class="red-text">(required for email receipt)</span>
              </label>
              <input
                type="email"
                id="forte2-email"
                name="forte2-email"
                v-model="email"
                :required="emailReceipt"
                :aria-required="emailReceipt ? 'true' : 'false'"
                placeholder="email@domain.com"
                @input="validateEmail"
                :class="{'invalid-input': emailError}"
              >
            </div>
            <div class="email-receipt">
              <v-checkbox
                v-model="emailReceipt"
                label="Email receipt"
                @change="onEmailReceiptToggle"
              ></v-checkbox>
            </div>
            <div v-if="emailError" class="error-text">
              <span v-if="emailReceipt">A valid email is required to email the receipt.</span>
              <span v-else>Please enter a valid email address.</span>
            </div>
          </v-col>
          <v-col cols="6" class="elevation-1 pa-4">
            <p class="text-center text-subtitle-1 header-class">Checkout Details</p>
            <v-row>
              <v-col cols="6" class="d-flex flex-column ml-4">
                <span v-for="fieldName in fieldNameLists" :key="fieldName">
                  <label class="input-text-size">{{ fieldName }}:</label>
                </span>
              </v-col>
              <v-col cols="4" class="d-flex flex-column align-end mr-4">
                <span v-for="(amt, i) in fieldAmtLists" :key="`amt-${i}`">
                  <label class="or-text">${{ amountFormat(amt) }}</label>
                </span>
              </v-col>
            </v-row>
            <v-divider class="my-3"></v-divider>
            <!-- Both candidate totals shown BEFORE verification (no tokenization) -->
            <v-row v-if="forte2PaymentType === 'CREDIT' && !isVerified" dense class="fee-row">
              <v-col cols="6" class="ml-4">
                <label class="input-text-size font-weight-bold green--text">
                  Total + (credit fee)
                </label>
              </v-col>
              <v-col cols="4" class="text-right mr-4">
                <label class="input-text-size font-weight-bold green--text">
                  ${{ amountFormat(creditQuote.total) }}
                </label>
              </v-col>
            </v-row>
            <v-row v-if="forte2PaymentType === 'CREDIT' && !isVerified" dense class="or-divider">
              <v-col cols="6" class="ml-4">
                <label class="input-text-size">-- or --</label>
              </v-col>
            </v-row>
            <v-row v-if="forte2PaymentType === 'CREDIT' && !isVerified" dense class="fee-row">
              <v-col cols="6" class="ml-4">
                <label class="input-text-size font-weight-bold green--text">
                  Total + (debit fee)</label>
              </v-col>
              <v-col cols="4" class="text-right mr-4">
                <label class="input-text-size font-weight-bold green--text">
                  ${{ amountFormat(debitQuote.total) }}
                </label>
              </v-col>
            </v-row>
            <!-- eCheck flat service fee from the location rate plan
                 (services.echeck.service_fee_amount). Flat, so it is known on dialog
                 open — no BIN lookup, no credit/debit ambiguity, no verify gating.
                 Mirrors the card branch's resolved fee/total layout. -->
            <v-row v-if="forte2PaymentType === 'ECHECK'" dense class="verified-fee-section">
              <v-col cols="12" class="text-center pa-3">
                <div class="verified-fee-details">
                  <span class="verified-label">Subtotal:</span>
                  <span class="verified-value">${{ amountFormat(subtotal) }}</span>
                </div>
                <div class="verified-fee-details">
                  <span class="verified-label">Fee:</span>
                  <span class="verified-value">${{ amountFormat(echeckQuote.fee) }}</span>
                </div>
                <div class="verified-total-details">
                  <span class="verified-label">Total to charge:</span>
                  <span class="verified-value font-weight-bold">
                    ${{ amountFormat(echeckQuote.total) }}</span>
                </div>
              </v-col>
            </v-row>
            <!-- eCheck verified state: bank name comes ONLY from /verify's issuer_bank -->
            <v-row
              v-if="forte2PaymentType === 'ECHECK' && isEcheckVerified && echeckPreauthResult === 'POS'"
              dense
              class="verified-fee-section"
            >
              <v-col cols="12" class="text-center pa-3">
                <v-alert type="info" dense text class="mb-2">
                  <div class="verified-card-type">ACCOUNT VERIFIED</div>
                </v-alert>
                <div v-if="echeckBankName" class="verified-fee-details">
                  <span class="verified-label">Bank:</span>
                  <span class="verified-value">{{ echeckBankName }}</span>
                </div>
                <div class="verified-total-details">
                  <span class="verified-label">Pre-auth:</span>
                  <span class="verified-value">{{ echeckPreauthResult }}</span>
                </div>
              </v-col>
            </v-row>
            <v-row
              v-if="forte2PaymentType === 'ECHECK' && isEcheckVerified && echeckPreauthResult !== 'POS'"
              dense
              class="verified-fee-section"
            >
              <v-col cols="12" class="text-center pa-3">
                <v-alert type="warning" dense text class="mb-2">
                  <div class="verified-card-type">ACCOUNT NOT CONFIRMED</div>
                </v-alert>
                <div class="verified-fee-details">
                  Forte could not verify this account.
                  You may proceed, or take another tender.
                </div>
                <div v-if="echeckBankName" class="verified-fee-details">
                  <span class="verified-label">Bank:</span>
                  <span class="verified-value">{{ echeckBankName }}</span>
                </div>
              </v-col>
            </v-row>
            <!-- Single resolved total after verification -->
            <v-row v-if="isVerified" dense class="verified-fee-section">
              <v-col cols="12" class="text-center pa-3">
                <v-alert type="info" dense text class="mb-2">
                  <div class="verified-card-type">{{ verifiedCardCategoryDisplay }} CARD</div>
                </v-alert>
                <div class="verified-fee-details">
                  <span class="verified-label">Fee:</span>
                  <span class="verified-value">${{ amountFormat(resolvedQuote.fee) }}</span>
                </div>
                <div class="verified-total-details">
                  <span class="verified-label">Total to charge:</span>
                  <span class="verified-value font-weight-bold">
                    ${{ amountFormat(resolvedQuote.total) }}</span>
                </div>
              </v-col>
            </v-row>
            <v-row justify="center" class="submit-button-row">
              <v-btn v-if="isVerified"
                class="input-text-size rounded elevation-2 cancel-verify-btn mr-2"
                color="grey darken-1"
                outlined
                :disabled="isProcessing"
                @click="cancelVerification"
              >
                Cancel
              </v-btn>
              <v-btn v-if="forte2PaymentType === 'CREDIT' && !isVerified"
                class="input-text-size rounded elevation-4 payment-submit-btn"
                color="green darken-1"
                dark
                :disabled="disableVerify"
                @click="verifyFee"
              >
                {{ isVerifying ? 'Verifying...' : 'Verify Fee' }}
              </v-btn>
              <v-btn v-if="isVerified"
                class="input-text-size rounded elevation-4 payment-submit-btn"
                color="blue darken-2"
                dark
                :disabled="isProcessing"
                @click="submitPayment"
              >
                {{ isProcessing ? 'Processing...' : 'Submit Payment' }}
              </v-btn>
              <v-btn
                v-if="forte2PaymentType === 'ECHECK' && !isEcheckVerified"
                class="input-text-size rounded elevation-4 payment-submit-btn mr-2"
                color="green darken-1"
                dark
                :disabled="disableEcheckVerify"
                @click="verifyAccount"
              >
                {{ isEcheckVerifying ? 'Verifying...' : 'Verify Account' }}
              </v-btn>
              <v-btn v-if="forte2PaymentType === 'ECHECK' && isEcheckVerified"
                class="input-text-size rounded elevation-4 payment-submit-btn"
                color="blue darken-2"
                dark
                :disabled="disableEcheckSubmit"
                @click="submitEcheckPayment"
              >
                {{ isProcessing ? 'Processing...' : 'Submit Payment' }}
              </v-btn>
            </v-row>
            <v-alert
              v-if="paymentMessage"
              :type="paymentMessageType"
              dense
              text
              class="mt-3"
            >
              {{ paymentMessage }}
            </v-alert>
            <div
              v-if="forte2PaymentType === 'CREDIT'"
              class="mt-auto pt-4 text-center card-group-section"
            >
              <img src="https://img.icons8.com/color/48/000000/mastercard-logo.png" class="accepted-card-icon">
              <img src="https://img.icons8.com/color/48/000000/visa.png" class="accepted-card-icon">
              <img src="https://img.icons8.com/color/48/000000/discover.png" class="accepted-card-icon">
              <img src="https://img.icons8.com/color/48/000000/amex.png" class="accepted-card-icon">
            </div>
          </v-col>
        </v-row>
        <v-row v-if="forte2PaymentType === 'CREDIT' && !isVerified">
          <v-col class="adjusted-text">
            * Fee calculations: Credit ({{ creditPercentage }}%)
            | Debit ({{ debitPercentage }}%)
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions class="justify-end">
        <v-btn text @click="handleClose">Close</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  mapState,
  mapActions,
} from 'vuex';
import { amountFormat } from '../../util/shared/vue-global';
import {
  loadForte2Script,
  createForte2Token,
  createForte2EcheckToken,
  callForte2Verify,
  computeForte2Fee,
  computeForte2EcheckQuote,
  processForte2Payment,
} from '../../util/processCreditCard/forte2.util';
import { clients } from '../../util/clients';

const { backendRest } = clients.direct;

export default {
  name: 'Forte2PaymentDialog',
  props: {
    fieldNameLists: {
      type: Array,
      default: () => [],
    },
  },
  data: () => ({
    cardNumber: null,
    expireMonth: null,
    expireYear: null,
    cvv: null,
    nameOnCard: null,
    phoneNumber: null,
    email: null,
    phoneNumberError: false,
    emailError: false,
    emailReceipt: false,
    paymentMessage: null,
    paymentMessageType: 'error',
    scriptLoaded: false,
    verifiedCardType: null,
    // eCheck form fields. confirmAccountNumber is browser-only: it is compared against
    // accountNumber here and NEVER enters the store or any outgoing payload.
    routingNumber: '',
    accountNumber: '',
    confirmAccountNumber: '',
    accountType: '',
    nameOnAccount: '',
    routingNumberError: false,
    accountMismatchError: false,
    // Which bank field the clerk is currently typing in. Routing/account inputs display dots
    // unless they are the focused field, so shoulder-surfing a filled-in form (or a screenshot
    // of one) never exposes the account details. See maskedEcheckValue.
    focusedEcheckField: '',
    // Latches true on an approved eCheck sale so a second Submit click cannot re-charge with the
    // still-valid (60 min) one-time token.
    echeckPaymentComplete: false,
  }),
  computed: {
    ...mapState('common/forte2Payment', [
      'forte2PaymentData',
      'forte2PaymentType',
      'isProcessing',
      'isVerifying',
      'isVerified',
      'verifiedToken',
      'verifiedCardCategory',
      'isEcheckVerifying',
      'isEcheckVerified',
      'verifiedEcheckToken',
      'echeckBankName',
      'echeckPreauthResult',
    ]),
    // Only meaningful while Forte2 stays TAX-only (see PaymentDetails.vue's `isForte2`
    // excludedPrograms list). If OD/Misc/Mtg Forte2 support ships, this needs a paymentContext
    // guard like PayrixPaymentDialog.vue has, or a stale tax cart could get attached as
    // taxRecords to an unrelated program's receipt.
    ...mapState({
      taxDetails: (state) => state.tax.taxPayment.taxDetails,
    }),
    isDialogOpen: {
      get() {
        return this.$store.state.common.forte2Payment.isDialogOpen;
      },
      set(val) {
        this.$store.commit('common/forte2Payment/setDialogOpen', val);
      },
    },
    subtotal() {
      return (this.forte2PaymentData && this.forte2PaymentData.totalAmount) || 0;
    },
    creditPercentage() {
      return (this.forte2PaymentData && this.forte2PaymentData.creditFeePercentage) || 0;
    },
    debitPercentage() {
      return (this.forte2PaymentData && this.forte2PaymentData.debitFeePercentage) || 0;
    },
    creditQuote() {
      return computeForte2Fee({
        cardCategory: 'C',
        subtotal: this.subtotal,
        creditPercentage: this.creditPercentage,
        debitPercentage: this.debitPercentage,
      });
    },
    debitQuote() {
      return computeForte2Fee({
        cardCategory: 'D',
        subtotal: this.subtotal,
        creditPercentage: this.creditPercentage,
        debitPercentage: this.debitPercentage,
      });
    },
    // resolved fee/total once the card_category is known
    resolvedQuote() {
      return computeForte2Fee({
        cardCategory: this.verifiedCardCategory,
        subtotal: this.subtotal,
        creditPercentage: this.creditPercentage,
        debitPercentage: this.debitPercentage,
      });
    },
    // Flat eCheck fee from the rate plan; 0 when unconfigured (backfill-safe).
    echeckFee() {
      return (this.forte2PaymentData && this.forte2PaymentData.forte2EcheckFee) || 0;
    },
    // eCheck fee/total. Flat, so it is known on open — no tokenization gating.
    echeckQuote() {
      return computeForte2EcheckQuote({
        subtotal: this.subtotal,
        echeckFee: this.echeckFee,
      });
    },
    fieldAmtLists() {
      // eCheck has a FLAT fee (shown in the breakdown below), not per-card credit/debit
      // candidate fees — so the top checkout list stays a single Amount Due row. Card
      // keeps its 3-row shape (subtotal + both candidate fees).
      if (this.forte2PaymentType === 'ECHECK') {
        return [this.subtotal];
      }
      return [this.subtotal, this.creditQuote.fee, this.debitQuote.fee];
    },
    verifiedCardCategoryDisplay() {
      return this.verifiedCardCategory === 'D' ? 'DEBIT' : 'CREDIT';
    },
    disableVerify() {
      return !this.scriptLoaded || this.isVerifying || this.phoneNumberError || this.emailError;
    },
    echeckFieldsValid() {
      const routingDigits = (this.routingNumber || '').replace(/\D/g, '');
      const account = (this.accountNumber || '').trim();
      const confirmAccount = (this.confirmAccountNumber || '').trim();
      const holder = (this.nameOnAccount || '').trim();
      const emailTrimmed = (this.email || '').trim();
      const emailValid = this.emailReceipt
        ? emailTrimmed !== '' && !this.emailError
        : !this.emailError;
      return routingDigits.length === 9
        && account !== ''
        && account === confirmAccount
        && this.accountType !== ''
        && holder !== ''
        && emailValid
        && !this.phoneNumberError;
    },
    // Bank fields are frozen while a verify or a sale is in flight, and for good once the charge
    // is approved — an edit mid-verify would otherwise desync the OTT from what is on screen.
    echeckInputsLocked() {
      return this.isEcheckVerifying || this.isProcessing || this.echeckPaymentComplete;
    },
    disableEcheckVerify() {
      return !this.scriptLoaded || this.isEcheckVerifying || !this.echeckFieldsValid;
    },
    disableEcheckSubmit() {
      if (this.isProcessing || !this.scriptLoaded) return true;
      // The OTT stays valid for 60 min, so a second click after an approval would happily debit
      // the account again. One approved charge per dialog.
      if (this.echeckPaymentComplete) return true;
      return !this.isEcheckVerified;
    },
  },
  beforeDestroy() {
    this.resetForte2DialogState();
  },
  mounted() {
    const cdnUrl = (this.forte2PaymentData && this.forte2PaymentData.forte2CdnUrl) || '';
    loadForte2Script(cdnUrl)
      .then(() => {
        this.scriptLoaded = true;
      })
      .catch((error) => {
        console.error('Failed to load Forte.js', error);
        this.paymentMessage = 'Unable to load the payment library. Please try again.';
        this.paymentMessageType = 'error';
      });
  },
  methods: {
    ...mapActions('common/forte2Payment', [
      'toggleDialog',
      'setIsProcessing',
      'setIsVerifying',
      'setVerified',
      'setIsEcheckVerifying',
      'setEcheckVerified',
      'clearVerification',
      'resetForte2DialogState',
      'setForte2PaymentResult',
      'setForte2PaymentSuccessFlag',
    ]),
    amountFormat,
    validatePhoneNumber() {
      const validPattern = /^[\d()-]{0,14}$/;
      const digits = this.phoneNumber ? this.phoneNumber.replace(/\D/g, '') : '';
      this.phoneNumberError = !!this.phoneNumber
        && (!validPattern.test(this.phoneNumber) || digits.length < 10);
    },
    validateEmail() {
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const emailTrimmed = this.email ? this.email.trim() : '';
      if (this.emailReceipt) {
        this.emailError = emailTrimmed === '' || !emailPattern.test(emailTrimmed);
      } else {
        this.emailError = emailTrimmed !== '' && !emailPattern.test(emailTrimmed);
      }
    },
    onEmailReceiptToggle(value) {
      this.emailReceipt = !!value;
      this.validateEmail();
    },
    splitName() {
      const fullName = (this.nameOnCard || '').trim();
      if (!fullName) return { firstName: '', lastName: '' };
      const parts = fullName.split(/\s+/);
      const firstName = parts.shift() || '';
      const lastName = parts.join(' ');
      return { firstName, lastName };
    },
    // eCheck sibling of splitName. Kept separate so the card path's splitName stays untouched.
    splitAccountHolderName() {
      const fullName = (this.nameOnAccount || '').trim();
      if (!fullName) return { firstName: '', lastName: '' };
      const parts = fullName.split(/\s+/);
      const firstName = parts.shift() || '';
      const lastName = parts.join(' ');
      return { firstName, lastName };
    },
    validateRoutingNumber() {
      const digits = this.routingNumber ? this.routingNumber.replace(/\D/g, '') : '';
      this.routingNumberError = !!this.routingNumber && digits.length !== 9;
    },
    validateAccountMatch() {
      const account = (this.accountNumber || '').trim();
      const confirmAccount = (this.confirmAccountNumber || '').trim();
      this.accountMismatchError = confirmAccount !== '' && account !== confirmAccount;
    },
    // Identity of the bank details an OTT is minted against. Captured before the verify
    // round-trip and re-checked after it, so an edit made WHILE verify is in flight can never
    // bind a token to an account the clerk no longer has on screen.
    echeckFieldsSnapshot() {
      return JSON.stringify([this.routingNumber, this.accountNumber, this.accountType]);
    },
    // A verified OTT belongs to the bank details as they were AT verify time, and the sale
    // reuses it. Editing any bank field afterwards would otherwise charge the OLD account,
    // so any edit drops back to unverified and the clerk must re-verify.
    clearEcheckVerificationOnEdit() {
      if (this.isEcheckVerified) {
        this.clearVerification();
        this.paymentMessage = null;
      }
    },
    onEcheckFieldInput() {
      this.validateRoutingNumber();
      this.validateAccountMatch();
      this.clearEcheckVerificationOnEdit();
    },
    // Bank details show as dots unless the clerk is typing in that very field. Masked by hand
    // rather than with type="password" because Chrome's password manager autofills saved
    // credentials into ANY password input (it ignores autocomplete="off" there), and rather
    // than with -webkit-text-security because Firefox only supports that from v125.
    maskedEcheckValue(field) {
      const value = this[field] || '';
      if (this.focusedEcheckField === field) return value;
      return '•'.repeat(value.length);
    },
    // Consequence of masking the DISPLAYED value: these inputs can't use v-model, since the
    // element's value is dots whenever the field is blurred. The real value lives only here.
    onMaskedEcheckInput(field, event) {
      this[field] = event.target.value;
      this.onEcheckFieldInput();
    },
    async verifyFee() {
      this.paymentMessage = null;
      this.validatePhoneNumber();
      this.validateEmail();
      if (this.phoneNumberError || this.emailError) return;
      if (!this.cardNumber || !this.expireMonth || !this.expireYear || !this.cvv) {
        this.paymentMessage = 'Please complete the card details.';
        this.paymentMessageType = 'error';
        return;
      }
      const apiLoginId = (this.forte2PaymentData && this.forte2PaymentData.forte2ApiLoginId) || '';
      this.setIsVerifying(true);
      try {
        const response = await createForte2Token({
          apiLoginId,
          cardNumber: this.cardNumber,
          expireMonth: this.expireMonth,
          expireYear: this.expireYear,
          cvv: this.cvv,
        });
        this.setVerified({
          token: response.onetime_token,
          cardCategory: response.card_category,
        });
        this.verifiedCardType = response.card_type || null;
        this.setIsVerifying(false);
      } catch (error) {
        // error callback rejects with response_description
        this.paymentMessage = `Verification failed: ${error.message}`;
        this.paymentMessageType = 'error';
        this.setIsVerifying(false);
      }
    },
    cancelVerification() {
      this.clearVerification();
      this.verifiedCardType = null;
      this.paymentMessage = null;
    },
    async verifyAccount() {
      this.paymentMessage = null;
      this.validateRoutingNumber();
      this.validateAccountMatch();
      this.validatePhoneNumber();
      this.validateEmail();
      if (!this.echeckFieldsValid) {
        this.paymentMessage = 'Please complete the bank account details.';
        this.paymentMessageType = 'error';
        return;
      }
      // Forte rejects a verify with a zero authorization_amount.
      if (!(this.subtotal > 0)) {
        this.paymentMessage = 'A payment amount greater than zero is required to verify.';
        this.paymentMessageType = 'error';
        return;
      }
      const apiLoginId = (this.forte2PaymentData && this.forte2PaymentData.forte2ApiLoginId) || '';
      const { firstName, lastName } = this.splitAccountHolderName();
      // Snapshot the bank details this verify is about, so we can prove on the way back that they
      // are still the ones on screen (the inputs are disabled while verifying, but an autofill or
      // a paste that lands before the disable paints must not slip through either).
      const verifyingSnapshot = this.echeckFieldsSnapshot();
      this.setIsEcheckVerifying(true);
      try {
        // Tokenize in the browser: raw account/routing NEVER reach our backend.
        const tokenResponse = await createForte2EcheckToken({
          apiLoginId,
          accountNumber: this.accountNumber,
          routingNumber: this.routingNumber,
          accountType: this.accountType,
        });
        const data = await callForte2Verify({
          onetimeToken: tokenResponse.onetime_token,
          authorizationAmount: this.subtotal,
          accountType: this.accountType,
          accountHolder: this.nameOnAccount,
          firstName,
          lastName,
          paymentConfig: this.forte2PaymentData || {},
        });
        // /verify answers 200 for approve/decline/error alike — branch on response_type.
        const result = (data && data.response) || {};
        const echeck = (data && data.echeck) || {};
        if (result.response_type === 'A') {
          // If the bank details changed while this verify was in flight, the OTT we just got back
          // belongs to the PREVIOUS account. Binding it would let Submit debit an account the
          // clerk can no longer see. Drop the result on the floor and make her verify again.
          if (this.echeckFieldsSnapshot() !== verifyingSnapshot) {
            this.paymentMessageType = 'error';
            this.paymentMessage = 'Bank details changed during verification. Please verify again.';
            return;
          }
          const preauth = String(result.preauth_result || '').trim().toUpperCase();
          if (preauth === 'NEG') {
            this.paymentMessageType = 'error';
            this.paymentMessage = 'Account failed verification. Forte reports this account as invalid or flagged. Re-check the numbers, or take another tender.';
          } else {
            this.setEcheckVerified({
              token: tokenResponse.onetime_token,
              bankName: echeck.issuer_bank || '',
              preauthResult: preauth || 'UNK',
            });
          }
        } else if (result.response_type === 'D') {
          this.paymentMessageType = 'error';
          this.paymentMessage = `Account could not be validated: ${result.response_desc || 'Check the details and try again.'}`;
        } else {
          this.paymentMessageType = 'error';
          this.paymentMessage = `Verification error: ${result.response_desc || 'Unable to verify the account.'}`;
        }
      } catch (error) {
        console.error('Forte2 eCheck verification error', error);
        this.paymentMessageType = 'error';
        this.paymentMessage = `Verification failed: ${error.message}`;
      } finally {
        this.setIsEcheckVerifying(false);
      }
    },
    async submitPayment() {
      if (this.isProcessing) return;
      this.paymentMessage = null;
      const { firstName, lastName } = this.splitName();
      // Hoisted so the captured taxccpayments record reuses the SAME referenceId we charge with.
      const referenceId = `TAX-${Date.now()}`;
      this.setIsProcessing(true);
      try {
        const data = await processForte2Payment({
          onetimeToken: this.verifiedToken,
          // The util computes authorization_amount = subtotal + serviceFeeAmount.
          // resolvedQuote.fee is card-category-driven (credit vs debit rate).
          subtotal: this.subtotal,
          serviceFeeAmount: this.resolvedQuote.fee,
          firstName,
          lastName,
          referenceId,
          phone: this.phoneNumber || undefined,
          email: this.email || undefined,
          company: (this.forte2PaymentData && this.forte2PaymentData.ownerName) || undefined,
          paymentConfig: this.forte2PaymentData || {},
          cardType: this.verifiedCardType,
        });

        const result = (data && data.response) || {};
        // Branch ONLY on response_type — never on HTTP status.
        if (result.response_type === 'A') {
          this.paymentMessageType = 'success';
          const txnId = result.transaction_id || (data && data.transaction_id) || '';
          this.paymentMessage = `Payment approved.${txnId ? ` Transaction: ${txnId}` : ''}`;
          // Capture everything the forte2PosReqMapping builder will need BEFORE the dialog can be
          // closed/reset, then raise the success flag LAST so the parent watcher sees a fully
          // populated store. The /forte2/process response echoes only billing_address.first_name/
          // last_name (no street/city/state/zip) — those fall back in the builder per Forte1 parity.
          // No optional chaining (Vue 2.6 / project lint): guard with && instead.
          const billing = result.billing_address || {};
          const capturedFirstName = billing.first_name || firstName;
          const capturedLastName = billing.last_name || lastName;
          this.setForte2PaymentResult({
            authorizationAmount: this.resolvedQuote.total,
            referenceId,
            cardholderName: `${capturedFirstName} ${capturedLastName}`.trim() || this.nameOnCard || '',
            billingFirstName: capturedFirstName,
            billingLastName: capturedLastName,
            email: this.email || '',
            phone: this.phoneNumber || '',
            principalAmount: this.subtotal,
            processingFee: this.resolvedQuote.fee,
            cardType: this.verifiedCardType,
          });

          try {
            await this.generateReceipt({
              transactionId: txnId,
              cardholderName: `${capturedFirstName} ${capturedLastName}`.trim() || this.nameOnCard || '',
              cardType: this.verifiedCardType,
              paymentType: 'CREDIT',
              total: this.resolvedQuote.total,
              fee: this.resolvedQuote.fee,
              emailReceipt: this.emailReceipt,
              emailAddress: this.email,
            });
          } catch (receiptErr) {
            console.error('[FORTE2] receipt generation failed (payment already approved):', receiptErr);
          }

          this.setForte2PaymentSuccessFlag({ flag: true, transactionId: txnId });
          // console.info('(Forte2PaymentDialog.vue): submitPayment()=> ', this.paymentMessage);
        } else if (result.response_type === 'D') {
          this.paymentMessageType = 'error';
          this.paymentMessage = `Payment declined: ${result.response_desc || 'Card was declined.'}`;
        } else {
          this.paymentMessageType = 'error';
          this.paymentMessage = `Payment error: ${result.response_desc || 'Unable to process payment.'}`;
        }
      } catch (error) {
        console.error('Forte2 payment submission error', error);
        this.paymentMessageType = 'error';
        this.paymentMessage = 'Payment could not be processed. Please try again.';
      } finally {
        this.setIsProcessing(false);
      }
    },
    async submitEcheckPayment() {
      if (this.isProcessing) return;
      this.paymentMessage = null;
      if (!this.echeckFieldsValid) {
        this.paymentMessageType = 'error';
        this.paymentMessage = 'Please complete the bank account details.';
        return;
      }
      if (!this.isEcheckVerified) {
        this.paymentMessageType = 'error';
        this.paymentMessage = 'Please verify the account before submitting.';
        return;
      }
      const { firstName, lastName } = this.splitAccountHolderName();
      // Hoisted so the captured taxccpayments record reuses the SAME referenceId we charge with.
      const referenceId = `TAX-${Date.now()}`;
      this.setIsProcessing(true);
      try {
        const data = await processForte2Payment({
          onetimeToken: this.verifiedEcheckToken,
          // Flat eCheck fee from the rate plan; the util assembles
          // authorization_amount = subtotal + fee in integer cents.
          subtotal: this.subtotal,
          serviceFeeAmount: this.echeckQuote.fee,
          firstName,
          lastName,
          referenceId,
          phone: this.phoneNumber || undefined,
          email: this.email || undefined,
          company: (this.forte2PaymentData && this.forte2PaymentData.ownerName) || undefined,
          paymentConfig: this.forte2PaymentData || {},
          paymentType: 'ECHECK',
          accountType: this.accountType,
          accountHolder: this.nameOnAccount,
        });

        const result = (data && data.response) || {};
        // Branch ONLY on response_type — never on HTTP status.
        if (result.response_type === 'A') {
          // Latch BEFORE the awaits below: the funds are committed, and nothing that follows
          // (receipt, persistence) may leave Submit clickable a second time.
          this.echeckPaymentComplete = true;
          this.paymentMessageType = 'success';
          const txnId = result.transaction_id || (data && data.transaction_id) || '';
          this.paymentMessage = `Payment approved.${txnId ? ` Transaction: ${txnId}` : ''}`;
          const billing = result.billing_address || {};
          const capturedFirstName = billing.first_name || firstName;
          const capturedLastName = billing.last_name || lastName;
          // cardType stays null: persistence routes specialPaymentType off forte2PaymentType,
          // so an eCheck lands on ECHECK_FORTE2 without going through the card-brand mapper.
          this.setForte2PaymentResult({
            authorizationAmount: this.echeckQuote.total,
            referenceId,
            cardholderName: `${capturedFirstName} ${capturedLastName}`.trim() || this.nameOnAccount || '',
            billingFirstName: capturedFirstName,
            billingLastName: capturedLastName,
            email: this.email || '',
            phone: this.phoneNumber || '',
            principalAmount: this.subtotal,
            processingFee: this.echeckQuote.fee,
            cardType: null,
          });

          try {
            // No card brand on an eCheck — paymentType, not cardType, is what routes the receipt
            // to its eCheck Type line, the same way persistence routes off forte2PaymentType.
            await this.generateReceipt({
              transactionId: txnId,
              cardholderName: `${capturedFirstName} ${capturedLastName}`.trim() || this.nameOnAccount || '',
              cardType: null,
              paymentType: 'ECHECK',
              total: this.echeckQuote.total,
              fee: this.echeckQuote.fee,
              emailReceipt: this.emailReceipt,
              emailAddress: this.email,
            });
          } catch (receiptErr) {
            console.error('[FORTE2] receipt generation failed (payment already approved):', receiptErr);
          }

          this.setForte2PaymentSuccessFlag({ flag: true, transactionId: txnId });
        } else if (result.response_type === 'D') {
          this.paymentMessageType = 'error';
          this.paymentMessage = `Payment declined: ${result.response_desc || 'Account was declined.'}`;
        } else {
          this.paymentMessageType = 'error';
          this.paymentMessage = `Payment error: ${result.response_desc || 'Unable to process payment.'}`;
        }
      } catch (error) {
        console.error('Forte2 eCheck payment submission error', error);
        this.paymentMessageType = 'error';
        this.paymentMessage = 'Payment could not be processed. Please try again.';
      } finally {
        this.setIsProcessing(false);
      }
    },
    async generateReceipt({
      transactionId, cardholderName, cardType, paymentType, total, fee, emailReceipt, emailAddress,
    }) {
      const payload = {
        transactionId,
        cardholderName,
        cardType,
        paymentType,
        total,
        fee,
        emailReceipt,
        emailAddress,
        // Lets the backend attach the "Email sent to ..." comment to the parcels actually
        // paid, instead of a comment with no taxYear/taxId at all.
        taxRecords: (this.taxDetails || [])
          .filter((item) => item && item.taxYear && item.taxId)
          .map((item) => ({ taxYear: item.taxYear, taxId: item.taxId })),
      };
      const receiptResponse = await backendRest.post(
        '/tax/ccPayments/forte2/taxpayer-receipt',
        payload,
        { responseType: 'blob' },
      );
      const blob = new Blob([receiptResponse.data], { type: 'application/pdf' });
      const blobUrl = window.URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
    },
    handleClose() {
      this.cardNumber = null;
      this.expireMonth = null;
      this.expireYear = null;
      this.cvv = null;
      this.nameOnCard = null;
      this.phoneNumber = null;
      this.email = null;
      this.phoneNumberError = false;
      this.emailError = false;
      this.emailReceipt = false;
      this.paymentMessage = null;
      this.paymentMessageType = 'error';
      this.verifiedCardType = null;
      this.routingNumber = '';
      this.accountNumber = '';
      this.confirmAccountNumber = '';
      this.accountType = '';
      this.nameOnAccount = '';
      this.routingNumberError = false;
      this.accountMismatchError = false;
      this.focusedEcheckField = '';
      this.echeckPaymentComplete = false;
      // Partial reset only: clears card-entry/verify state and closes the dialog, but PRESERVES
      // paymentSuccessFlag, activeItemIndex, and the captured result so a pending save can still
      // write the taxccpayments record (mirrors Payrix). The post-success state is fully cleared
      // by the mixin AFTER that write. resetForte2DialogState also sets isDialogOpen=false, so the
      // explicit toggleDialog(false) below is redundant but kept for clarity/parity with Payrix.
      this.resetForte2DialogState();
      this.toggleDialog(false);
    },
  },
};
</script>

<style scoped>
.title-position {
  padding: 0.5rem 0.5rem 1.25rem 0.25rem !important;
  margin-left: -0.25rem;
  margin-top: -0.5rem;
  font-size: 1.25rem;
}
.header-class {
  background-color: #363a40;
  color: white;
}
#forte2-card-number,
#forte2-exp-month,
#forte2-exp-year,
#forte2-cvv,
#forte2-name,
#forte2-routing-number,
#forte2-account-number,
#forte2-confirm-account-number,
#forte2-account-type,
#forte2-name-on-account,
#forte2-phone-number,
#forte2-email {
  width: 100%;
  height: 2.5rem;
  padding: 0.5rem 0;
  margin-bottom: 0.6275rem;
  border: none;
  border-bottom: 0.0625rem solid #ccc;
  border-radius: 0;
  font-size: 0.875rem;
  box-sizing: border-box;
  outline: none;
  transition: border-color 0.3s;
  background-color: transparent;
}
.input-field {
  padding-bottom: 1.25rem;
}
#forte2-card-number:focus,
#forte2-exp-month:focus,
#forte2-exp-year:focus,
#forte2-cvv:focus,
#forte2-name:focus,
#forte2-routing-number:focus,
#forte2-account-number:focus,
#forte2-confirm-account-number:focus,
#forte2-account-type:focus,
#forte2-name-on-account:focus,
#forte2-phone-number:focus,
#forte2-email:focus {
  border-bottom: 0.125rem solid #4285f4;
}
#tmc-blue {
  color: #1976D2;
}
.invalid-input {
  border-bottom: 0.125rem solid red !important;
}
.error-text {
  color: red;
  font-size: 0.75rem;
}
.red-text {
  color: red;
}
.fee-row {
  margin-top: 0 !important;
  margin-bottom: 0 !important;
}
.payment-submit-btn.v-btn.v-btn--disabled {
  background-color: #044007 !important;
  color: white !important;
  opacity: 0.8;
}
.accepted-card-icon {
  height: 3rem;
  margin: 0 0.3rem;
}
.adjusted-text {
  font-size: 0.875rem;
}
.input-field label {
  font-size: 0.625rem;
  color: darkslategray;
  font-weight: 500;
}
.or-divider {
  margin-top: 0.125rem !important;
  margin-bottom: 0.125rem !important;
  height: 1.5rem;
}
.or-text {
  line-height: 1;
  padding: 0;
}
.card-group-section {
  margin-top: 3rem !important;
}
.submit-button-row {
  margin-top: 2rem !important;
  margin-bottom: 2rem !important;
}
.verified-fee-section {
  background-color: #e3f2fd;
  border-radius: 8px;
  margin: 0.5rem 0;
}
.verified-card-type {
  font-size: 1rem;
  font-weight: bold;
  color: #1565c0;
}
.verified-fee-details,
.verified-total-details {
  font-size: 0.95rem;
  margin: 0.25rem 0;
}
.verified-label {
  color: #666;
  margin-right: 0.5rem;
}
.verified-value {
  color: #1565c0;
  font-size: 1rem;
}
.verified-total-details .verified-value {
  font-size: 1.1rem;
}
.cancel-verify-btn {
  min-width: 80px;
}
</style>
